// A generic cross-instance memo for an expensive derivation of a page's ADF,
// shared so N consumers of the same document parse + walk it once, not N times.
// Keyed by a small id (e.g. contentId) with a reference-compared `token` (e.g.
// the ADF string) — the multi-MB body is never hashed or used as a Map key, and
// a new document version is a natural miss.

const DEFAULT_MAX_ENTRIES = 4;

type CacheEntry<T> = { token: unknown; value: T };

export type AdfDerivationMemo<T> = {
	derive: (key: string, token: unknown, compute: () => T) => T;
	clear: () => void;
};

export const createAdfDerivationMemo = <T>(
	maxEntries: number = DEFAULT_MAX_ENTRIES,
): AdfDerivationMemo<T> => {
	const store = new Map<string, CacheEntry<T>>();

	return {
		derive: (key, token, compute) => {
			const existing = store.get(key);
			if (existing && Object.is(existing.token, token)) {
				// Re-insert to mark most-recently-used (Map preserves insertion order).
				store.delete(key);
				store.set(key, existing);
				return existing.value;
			}

			const value = compute();

			if (store.has(key)) {
				store.delete(key);
			} else if (store.size >= maxEntries) {
				const oldest = store.keys().next().value;
				if (oldest !== undefined) {
					store.delete(oldest);
				}
			}
			store.set(key, { token, value });

			return value;
		},
		clear: () => {
			store.clear();
		},
	};
};
